import React, { useCallback, useEffect, useState } from 'react';
import type { ProjectPlan } from '../../types';
import { buildRollbackSegments, rollbackSegmentsCross } from '../../utils/approvalEntanglementUi';

export interface ApprovalRollbackOverlayProps {
  open: boolean;
  projectPlan: ProjectPlan | null;
  /** Scroll container (e.g. TableContainer) for coordinate space */
  scrollContainerRef: React.RefObject<HTMLElement | null>;
}

/**
 * SVG overlay on the left side of the planning table showing APPROVE → Reject rollback links.
 * Invalid segments (rule violations) render red; crossing lines render orange/red.
 */
export const ApprovalRollbackOverlay: React.FC<ApprovalRollbackOverlayProps> = ({
  open,
  projectPlan,
  scrollContainerRef,
}) => {
  const [layout, setLayout] = useState<{
    width: number;
    height: number;
    lines: Array<{
      key: string;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      stroke: string;
      crosses: boolean;
    }>;
  } | null>(null);

  const recalc = useCallback(() => {
    if (!open || !projectPlan || !scrollContainerRef.current) {
      setLayout(null);
      return;
    }
    const container = scrollContainerRef.current;
    const cRect = container.getBoundingClientRect();
    const segs = buildRollbackSegments(projectPlan);
    if (segs.length === 0) {
      setLayout({ width: 56, height: Math.max(container.scrollHeight, cRect.height), lines: [] });
      return;
    }

    const raw: Array<{
      key: string;
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      baseValid: boolean;
    }> = [];

    segs.forEach((s, i) => {
      const approveEl = container.querySelector(
        `tr[data-planning-row-id="${s.approveRowId}"]`
      ) as HTMLElement | null;
      const rejectEl = container.querySelector(
        `tr[data-planning-row-id="${s.rejectRowId}"]`
      ) as HTMLElement | null;
      if (!approveEl || !rejectEl) {
        return;
      }
      const aRect = approveEl.getBoundingClientRect();
      const rRect = rejectEl.getBoundingClientRect();
      const y1 = rRect.top - cRect.top + container.scrollTop + rRect.height / 2;
      const y2 = aRect.top - cRect.top + container.scrollTop + aRect.height / 2;
      const x1 = 4 + i * 4;
      const x2 = 32 + i * 4;
      raw.push({
        key: `${s.approveActionId}-${s.rejectActionId}`,
        x1,
        y1,
        x2,
        y2,
        baseValid: s.valid,
      });
    });

    const lines = raw.map((L, i) => {
      let crosses = false;
      for (let j = 0; j < raw.length; j++) {
        if (i === j) {
          continue;
        }
        if (
          rollbackSegmentsCross(
            { x1: L.x1, y1: L.y1, x2: L.x2, y2: L.y2 },
            { x1: raw[j].x1, y1: raw[j].y1, x2: raw[j].x2, y2: raw[j].y2 }
          )
        ) {
          crosses = true;
          break;
        }
      }
      const stroke =
        !L.baseValid || crosses ? '#d32f2f' : '#1976d2';
      return { ...L, stroke, crosses };
    });

    setLayout({
      width: 56,
      height: Math.max(container.scrollHeight, cRect.height),
      lines,
    });
  }, [open, projectPlan, scrollContainerRef]);

  useEffect(() => {
    recalc();
  }, [recalc]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const el = scrollContainerRef.current;
    if (!el) {
      return undefined;
    }
    const ro = new ResizeObserver(() => recalc());
    ro.observe(el);
    el.addEventListener('scroll', recalc, { passive: true });
    const t = window.setInterval(recalc, 500);
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', recalc);
      window.clearInterval(t);
    };
  }, [open, recalc, scrollContainerRef]);

  if (!open || !layout) {
    return null;
  }

  return (
    <div
      style={{
        width: layout.width,
        minWidth: layout.width,
        position: 'relative',
        flexShrink: 0,
        alignSelf: 'stretch',
      }}
      aria-hidden
    >
      <svg
        width={layout.width}
        height={layout.height}
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          overflow: 'visible',
          pointerEvents: 'none',
        }}
      >
        {layout.lines.map((ln) => (
          <line
            key={ln.key}
            x1={ln.x1}
            y1={ln.y1}
            x2={ln.x2}
            y2={ln.y2}
            stroke={ln.stroke}
            strokeWidth={ln.crosses || ln.stroke === '#d32f2f' ? 2.5 : 1.5}
            strokeDasharray={ln.stroke === '#d32f2f' ? '4 3' : undefined}
          />
        ))}
      </svg>
    </div>
  );
};

export default ApprovalRollbackOverlay;
